/* eslint-disable camelcase */
import {
  ICombinationModel,
  IFilterObj
} from 'src/components/filters/models/filter-response.model';
import {
  CREATE_SKU,
  DELETE_SKU,
  DUPLICATE_SKU,
  EDIT_SKU,
  FETCH_SIMULATED_INNOVATION_DATA,
  GET_DUPLICATE_DIALOG_FILTERS,
  GET_INNOVATION_TABLE_DATA,
  GET_OVERWRITE_SKU_METADATA,
  GET_SKU_LIST_TABLE,
  GET_SKU_METADATA,
  OVERWRITE_SKU_METADATA,
  RESET_INNOVATION_SIMULATION,
  SAVE_INNOVATION_SIMULATION
} from 'src/constants';
import { IFieldResponse, axios } from 'src/lib';
import {
  IBulkDuplicateSkuPayload,
  ICreateSkuPayload,
  IDeleteSkuPayload,
  IDuplicateDialogFiltersPayload,
  IDuplicateSkuPayload,
  IEditSkuPayload,
  IGetInnovationDataPayload,
  ISimulateInnovationPayload
} from '../types/payload-models';

// api for fetching create sku metadata
export function fetchSkuMetadata(
  sourceId: number,
  moduleID: number,
  scenarioID: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${GET_SKU_METADATA}${sourceId}/${moduleID}/${scenarioID}/`, {
      filter_inputs: filters
    })
    .then<{
      combinations: ICombinationModel[];
      fields: IFieldResponse[];
      sku_name_list: any[];
    }>((response) => response.data)
    .then((response) => ({
      fields: response.fields.sort(
        (a, b) => a.placement_order - b.placement_order
      ),
      combinations: response.combinations,
      sku_name_list: response.sku_name_list
    }))
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function overwriteSkuMetadata(
  sourceId: number,
  moduleID: number,
  scenarioID: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${OVERWRITE_SKU_METADATA}${sourceId}/${moduleID}/${scenarioID}/`, {
      filter_inputs: filters
    })
    .then<{
      combinations: ICombinationModel[];
      fields: IFieldResponse[];
      sku_name_list: any[];
    }>((response) => response.data)
    .then((response) => ({
      fields: response.fields.sort(
        (a, b) => a.placement_order - b.placement_order
      ),
      combinations: response.combinations,
      sku_name_list: response.sku_name_list
    }))
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// api for fetching innovation table data
export function fetchInnovationTableData(payload: IGetInnovationDataPayload) {
  return axios
    .post(
      `${GET_INNOVATION_TABLE_DATA}${payload.sourceID}/${payload.moduleID}/${payload.scenarioID}/`,
      { filter_inputs: payload.filterInputs }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// api call for creating new sku
export function createSku(payload: ICreateSkuPayload) {
  // return 0;
  return axios
    .post(
      `${CREATE_SKU}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      { filter_inputs: payload.filter_inputs, new_sku_inputs: payload.newSkus }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function updateSku(payload: IEditSkuPayload) {
  return axios
    .post(
      `${EDIT_SKU}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      {
        filter_inputs: payload.filter_inputs,
        prevsku: payload.prevsku,
        new_sku_inputs: [payload.editedSku]
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function fetchOverwriteSkuMetadata(
  sourceId: number,
  moduleID: number,
  scenarioID: number,
  skuName
) {
  return axios
    .post(
      `${GET_OVERWRITE_SKU_METADATA}${sourceId || 1}/${moduleID || 1}/${
        scenarioID || 1
      }/`,
      {
        sku: skuName
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function updateOverwriteSku(payload: IEditSkuPayload) {
  return axios
    .post(
      `${OVERWRITE_SKU_METADATA}${payload.sourceID || 1}/${
        payload.moduleID || 1
      }/${payload.scenarioID || 1}/`,
      { data: payload.editedSku }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// api call for duplicating single sku
export function duplicateSku(payload: IDuplicateSkuPayload) {
  return axios
    .post(
      `${DUPLICATE_SKU}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      {
        filter_inputs: payload.filter_inputs,
        new_skus_to_duplicate: [
          { sku: payload.sku, count: 1, is_new_sku: true }
        ],
        existing_skus_to_duplicate: []
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// api call for duplicating bulk sku
export function fetchBulkDuplicatSkus(payload: IBulkDuplicateSkuPayload) {
  return axios
    .post(
      `${DUPLICATE_SKU}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      {
        filter_inputs: payload.filter_inputs,
        new_skus_to_duplicate: payload.bulkSkus.new_skus,
        existing_skus_to_duplicate: payload.bulkSkus.existing_skus
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function delSku(payload: IDeleteSkuPayload) {
  return axios
    .post(
      `${DELETE_SKU}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      {
        delete_skus: [
          {
            sku: payload.sku
          }
        ],
        filter_inputs: payload.filter_inputs
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function fetchDuplicateSkuPopupFilters(
  payload: ISimulateInnovationPayload
) {
  return axios
    .post(
      `${GET_DUPLICATE_DIALOG_FILTERS}${payload.sourceID || 1}/${
        payload.moduleID || 1
      }/${payload.scenarioID || 1}/`,
      {
        filter_inputs: payload.filterInputs
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

export function fetchDuplicateSkuTable(
  payload: IDuplicateDialogFiltersPayload
) {
  return axios
    .post(
      `${GET_SKU_LIST_TABLE}${payload.sourceID || 1}/${payload.moduleID || 1}/${
        payload.scenarioID || 1
      }/`,
      {
        // filter_inputs: payload.filterInputs,
        // duplicate_filter_inputs: payload.duplicateFilterInputs
        filter_inputs: payload.duplicateFilterInputs
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// handler for simulation api
export function fetchSimulationResults(payload: ISimulateInnovationPayload) {
  return axios
    .post(
      `${FETCH_SIMULATED_INNOVATION_DATA}${payload.sourceID || 1}/${
        payload.moduleID || 1
      }/${payload.scenarioID || 1}/`,
      {
        filter_inputs: payload.filterInputs
      }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e?.response?.data?.error || e?.message);
    });
}

// handler for save simulation
export function saveSimulatedInnovation(
  sourceId: number,
  moduleID: number,
  scenarioID: number
) {
  return axios
    .post(
      `${SAVE_INNOVATION_SIMULATION}${sourceId || 1}/${moduleID || 1}/${
        scenarioID || 1
      }/`
    )
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}

// handler for reset simulation
export function resetSimulationInnovation(payload: IGetInnovationDataPayload) {
  return axios
    .post(
      `${RESET_INNOVATION_SIMULATION}${payload.sourceID}/${payload.moduleID}/${payload.scenarioID}/`,
      { filter_inputs: payload.filterInputs }
    )
    .then((response) => response.data)
    .catch((e) => {
      throw Error(e.response.data.error);
    });
}
